import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AdvisorField, AuthSeparator } from '@client/components';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useBgaManagement } from '@client/hooks/useBgaManagement';
import { useCarrierSettings } from '@client/hooks/useCarrierSettings';
import { InsuranceCarrier } from '@shared';
import { CarrierSettingsCard } from './CarrierSettings';
import { BgaCard, BgaLoadingState } from './BgaManagement';
import { EmptyBgaState } from './BgaManagement';
import { CarrierSettingsLoadingState } from './CarrierSettings';
import { EmptyCarrierSettingsState } from './CarrierSettings';
import { LicenseList } from '@/features/licenses';

// Main Component
export function CarrierBGATab() {
  const { flags } = useFeatureFlags();
  const licensesManagementEnabled = flags.licensesManagement ?? false;
  const bgaManagement = useBgaManagement();
  const carrierSettings = useCarrierSettings();

  const isLoading = bgaManagement.isLoading || carrierSettings.isLoading;
  const bgasIsEmpty = bgaManagement.bgas.length === 0;
  const [selectedCarrierId, setSelectedCarrierId] = useState<number | null>(null);

  // Auto-select first carrier when carriers load
  useEffect(() => {
    if (carrierSettings.carriers.length > 0 && !selectedCarrierId) {
      setSelectedCarrierId(carrierSettings.carriers[0].insuranceCarrierId);
    }
  }, [carrierSettings.carriers, selectedCarrierId]);

  const selectedCarrier = carrierSettings.carriers.find(
    carrier => carrier.insuranceCarrierId === selectedCarrierId,
  );

  const handleAssignCarrier = (bgaOrganizationId: string, carrierId: InsuranceCarrier) => {
    bgaManagement.assignCarrierToBga(bgaOrganizationId, carrierId);
  };

  return (
    <div className="space-y-8">
      {licensesManagementEnabled && (
        <>
          <AdvisorField
            label=""
            description=""
            className="pt-4 sm:col-span-6"
          >
            <div className="pt-4">
              <LicenseList />
            </div>
          </AdvisorField>
          <AuthSeparator />
        </>
      )}

      <AdvisorField
        label=""
        description=""
        className="pt-4 sm:col-span-6"
      >
        <div className="space-y-6">
          <div className="flex flex-row items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-800">Carrier Appointments</h3>
              <p className="text-sm text-gray-700">
                Add and manage your BGA/IMO partnerships or direct appointments.
              </p>
            </div>
            <Button
              type="button"
              onClick={bgaManagement.addBga}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add carrier appointment
            </Button>
          </div>

          {isLoading ? (
            <BgaLoadingState />
          ) : bgasIsEmpty ? (
            <EmptyBgaState onAddBga={bgaManagement.addBga} />
          ) : (
            <div className="space-y-4">
              {bgaManagement.bgas.map(bga => (
                <BgaCard
                  key={bga.advisorOrganizationId}
                  bga={bga}
                  bgaManagement={bgaManagement}
                  carriers={carrierSettings.carriers}
                  onAssignCarrier={handleAssignCarrier}
                />
              ))}
            </div>
          )}
        </div>
      </AdvisorField>

      <AuthSeparator />
      <AdvisorField
        label="Carrier Products"
        description="Select which insurance products to offer from each the available carriers."
        className="pt-4 sm:col-span-6"
      >
        <div className="space-y-6 pt-4">
          {carrierSettings.isLoading ? (
            <CarrierSettingsLoadingState />
          ) : carrierSettings.carriers.length === 0 ? (
            <EmptyCarrierSettingsState />
          ) : (
            <div className="space-y-4">
              {/* Carrier Selector Dropdown */}
              <div className="relative max-w-md">
                <Select
                  value={selectedCarrierId?.toString() || ''}
                  onValueChange={value => setSelectedCarrierId(parseInt(value))}
                >
                  <SelectTrigger
                    id="carrier-select"
                    className="w-full text-sm text-gray-700"
                  >
                    <SelectValue placeholder="Select a carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {carrierSettings.carriers.map(carrier => (
                      <SelectItem
                        key={carrier.insuranceCarrierId}
                        value={carrier.insuranceCarrierId.toString()}
                      >
                        <div className="flex items-center gap-2">
                          <img
                            src={`/logos/${carrier.name.toLowerCase()}.svg`}
                            alt={carrier.name}
                            className="h-4 w-4"
                          />
                          <span>{carrier.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected Carrier Settings */}
              {selectedCarrier && (
                <Card>
                  <CardContent className="relative p-0">
                    <CarrierSettingsCard
                      carrier={selectedCarrier}
                      bga={bgaManagement.getBgaForCarrier(selectedCarrier.insuranceCarrierId)}
                      onToggleProduct={carrierSettings.toggleProduct}
                      onRemoveProduct={carrierSettings.removeProduct}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </AdvisorField>
    </div>
  );
}
